import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../shared/Sidebar";
import Header from "../shared/Header";
import { useTheme } from "../../contexts/ThemeContext";
import { useAlertWebSocket } from "../../contexts/WebSocketContext";
import { useModule } from "../../contexts/ModuleContext";
import { getItem, logout, hasScope, SCOPES } from "../../utils/authStorage";
import { parseJwt } from "../../utils/jwtDecoder";
import { toast } from "react-toastify";
import { fetchActiveDrones } from "../../services/deviceService";
import { fetchProjects } from "../../services/projectService";
import { Building2, ChevronDown } from "lucide-react";
import {
  fetchSurveyPlans,
  createSurveyPlan,
  updateSurveyPlan,
  deleteSurveyPlan,
  uploadSurveyPlan,
  importSurveyPlanKmz,
  fetchSurveyPlanKmz,
} from "../../services/surveyService";
import {
  flattenPlanItems,
  missionStats,
  validatePlan,
  deriveGeofence,
} from "../../utils/missionGenerators";
import SurveyPlanView from "./SurveyPlanView";
import ItemEditorPanel from "./ItemEditorPanel";
import DroneUploadBar, { FlightStatusStrip } from "./DroneUploadBar";
import PlansDrawer from "./PlansDrawer";
import PlansMenu from "./PlansMenu";
import "./ConstructionSurvey.css";

const DEFAULT_GEOFENCE = { enabled: false, type: "inclusion", action: "RTL", polygons: [] };
const FENCE_KINDS = ["fence_inclusion", "fence_exclusion"];
const DEFAULT_SETUP = { homeLocation: null, takeoffAltitude: 20, rtlAtEnd: true, cameraIntervalS: 0 };

const TOOL_BUTTONS = [
  { key: "pointer", label: "✥", title: "Select / edit shapes" },
  { key: "move_plan", label: "⤬ Move Plan", title: "Drag any item to shift the ENTIRE plan" },
  { key: "waypoint", label: "◉ WP", title: "Add waypoint: click map" },
  { key: "survey", label: "▦ Grid", title: "Draw survey area polygon" },
  { key: "corridor", label: "⇒ Corridor", title: "Draw corridor polyline" },
  { key: "structure", label: "⬠ Structure", title: "Draw structure footprint polygon" },
  { key: "fence_inclusion", label: "⛨ Fence+", title: "Draw inclusion geofence polygon" },
  { key: "fence_exclusion", label: "⛨ Fence−", title: "Draw exclusion geofence polygon" },
];
const PLANNING_TOOLS = TOOL_BUTTONS.filter(
  (b) => !["pointer", "move_plan"].includes(b.key)
).map((b) => b.key);
const MAPPING_KINDS = ["survey_grid", "corridor_scan", "structure_scan"];

function uid() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function ConstructionSurveyPage() {
  const { isDarkMode } = useTheme();
  const canWritePlans = hasScope(SCOPES.SURVEY_WRITE);
  const canFly = hasScope(SCOPES.SURVEY_FLY);
  const { activeProject, setActiveProject } = useModule();
  const [collapsed, setCollapsed] = useState(false);
  const { subscribe } = useAlertWebSocket();

  // plan state
  const [planName, setPlanName] = useState("Untitled Survey");
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [items, setItems] = useState([]);
  const [geofence, setGeofence] = useState(DEFAULT_GEOFENCE);
  const [setup, setSetup] = useState(DEFAULT_SETUP);
  const [selectedId, setSelectedId] = useState(null);
  const [activeTool, setActiveTool] = useState("pointer");
  const [fitBoundsKey, setFitBoundsKey] = useState(0);

  // project association (plans are created & saved per construction project)
  const [projects, setProjects] = useState([]);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);

  // adjustable split between map (left) and editor (right)
  const bodyRef = useRef(null);
  const [editorWidth, setEditorWidth] = useState(300);
  const dragStateRef = useRef(false);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragStateRef.current || !bodyRef.current) return;
      e.preventDefault();
      const rect = bodyRef.current.getBoundingClientRect();
      const w = Math.min(480, Math.max(260, rect.right - e.clientX));
      setEditorWidth(w);
    };
    const onUp = () => {
      if (!dragStateRef.current) return;
      dragStateRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const startResize = () => {
    dragStateRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  // fleet / plans / upload state
  const [drones, setDrones] = useState([]);
  const [selectedDroneId, setSelectedDroneId] = useState("");
  const [plansList, setPlansList] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [plansLoading, setPlansLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [flight, setFlight] = useState(null);
  const flightRef = useRef(null);
  flightRef.current = flight;

  const token = getItem("accessToken");
  const tokenPayload = token ? parseJwt(token) : null;
  const username =
    getItem("username") || tokenPayload?.username || tokenPayload?.preferred_username || "User";
  const rawRole = getItem("role") || tokenPayload?.role || tokenPayload?.roles?.[0] || "Operator";
  const role = rawRole.toLowerCase();
  const hasRoleFn = (allowedRoles) =>
    allowedRoles.flat().some((r) => typeof r === "string" && r.toLowerCase() === role);
  const handleSignOut = () => logout();

  /* ── derived mission data ───────────────────────────────────── */
  const waypoints = useMemo(() => flattenPlanItems(items), [items]);
  const stats = useMemo(() => missionStats(waypoints), [waypoints]);
  // effective geofence comes from fence items (+ legacy polygons for old plans)
  const effectiveGeofence = useMemo(() => deriveGeofence({ items, geofence }), [items, geofence]);
  const warnings = useMemo(
    () => validatePlan({ items, geofence: effectiveGeofence }, waypoints),
    [items, effectiveGeofence, waypoints]
  );
  const hasBlockingError = useMemo(
    () =>
      !items.length ||
      items.some(
        (it) =>
          it.kind !== "waypoint" &&
          !(it.polygon?.length >= 3) &&
          !(it.polyline?.length >= 2)
      ),
    [items]
  );
  const setupReady = useMemo(() => {
    const h = setup.homeLocation;
    return (
      !!h &&
      Number.isFinite(h.lat) &&
      Number.isFinite(h.lng) &&
      Math.abs(h.lat) <= 90 &&
      Math.abs(h.lng) <= 180 &&
      setup.takeoffAltitude > 0
    );
  }, [setup]);

  /* ── initial data ───────────────────────────────────────────── */
  const activeProjectId = activeProject?.numericId || activeProject?.id || "";
  const noProject = !activeProjectId;

  const reloadPlans = useCallback(() => {
    setPlansLoading(true);
    fetchSurveyPlans(activeProjectId)
      .then((data) => setPlansList(Array.isArray(data) ? data : data.results || []))
      .catch((e) => console.error("Failed to load survey plans:", e))
      .finally(() => setPlansLoading(false));
  }, [activeProjectId]);

  useEffect(() => {
    let isMounted = true;
    Promise.all([fetchActiveDrones(), fetchProjects()])
      .then(([d, projs]) => {
        if (!isMounted) return;
        setDrones(Array.isArray(d) ? d : []);
        const list = Array.isArray(projs) ? projs : [];
        setProjects(list);
        if (!activeProject || !activeProject.id) {
          const first = list[0];
          if (first) {
            setActiveProject({
              id: first.id,
              numericId: first.numericId,
              name: first.name,
              location: first.location,
              lastSurvey: first.lastSurvey || "Latest Survey",
              imageUrl: first.imageUrl || null,
            });
          }
        }
      })
      .catch((e) => console.error("Failed to load drones/projects:", e));
    return () => {
      isMounted = false;
    };
  }, [activeProject, setActiveProject]);

  useEffect(() => {
    reloadPlans();
  }, [reloadPlans]);

  /* ── websocket updates ──────────────────────────────────────── */
  // editing the plan invalidates what's staged on the vehicle
  useEffect(() => {
    setFlight((f) => (f && f.status === "uploaded" ? null : f));
  }, [items, setup, planName]);

  useEffect(() => {
    return subscribe((msg) => {
      if (!msg?.type?.startsWith("survey.")) return;
      if (msg.flight_id && flightRef.current && msg.flight_id !== flightRef.current.id) return;
      switch (msg.type) {
        case "survey.staged":
          setFlight((f) => ({
            ...(f || {}),
            id: msg.flight_id || f?.id,
            status: "uploaded",
            message: "Mission staged on vehicle — switch to AUTO to start.",
          }));
          toast.info("Mission uploaded — switch drone to AUTO mode to begin.");
          break;
        case "survey.progress":
          setFlight((f) => ({
            ...(f || {}),
            id: msg.flight_id || f?.id,
            status: msg.status || f?.status || "in_progress",
            current_waypoint: msg.current_waypoint ?? f?.current_waypoint,
            total_waypoints: msg.total_waypoints ?? f?.total_waypoints,
            message: msg.message,
          }));
          break;
        case "survey.completed":
          setFlight((f) => ({
            ...(f || {}),
            id: msg.flight_id || f?.id,
            status: "completed",
            message: "Survey completed.",
          }));
          toast.success("Construction survey completed.");
          break;
        case "survey.failed":
        case "survey.aborted":
          setFlight((f) => ({
            ...(f || {}),
            id: msg.flight_id || f?.id,
            status: msg.type === "survey.failed" ? "failed" : "aborted",
            message: msg.message,
          }));
          toast.error(`Survey ${msg.type === "survey.failed" ? "failed" : "aborted"}: ${msg.message || ""}`);
          break;
        default:
          break;
      }
    });
  }, [subscribe]);

  /* ── item CRUD ──────────────────────────────────────────────── */
  const itemsRef = useRef(items);
  itemsRef.current = items;

  /* ── undo / redo history (planning: items + geofence + setup) ─ */
  const pastRef = useRef([]); // older -> newer snapshots
  const futureRef = useRef([]); // newer -> older snapshots
  const gestureLockRef = useRef(false);

  const snapshotNow = () =>
    JSON.parse(JSON.stringify({ items, geofence, setup }));

  // push current state onto undo stack before a discrete mutation
  const commitUndoPoint = useCallback((applyFn) => {
    pastRef.current.push(snapshotNow());
    if (pastRef.current.length > 100) pastRef.current.shift();
    futureRef.current = [];
    applyFn();
  }, [items, geofence, setup]);

  // coalesce gesture (drag) edits into a single undo step
  const beginGesture = useCallback(() => {
    if (gestureLockRef.current) return;
    gestureLockRef.current = true;
    pastRef.current.push(snapshotNow());
    if (pastRef.current.length > 100) pastRef.current.shift();
    futureRef.current = [];
  }, [items, geofence, setup]);

  const endGesture = useCallback(() => {
    gestureLockRef.current = false;
  }, []);

  const applySnapshot = (s) => {
    setItems(s.items || []);
    setGeofence(s.geofence || DEFAULT_GEOFENCE);
    setSetup(s.setup || DEFAULT_SETUP);
  };

  const undo = useCallback(() => {
    const prev = pastRef.current.pop();
    if (!prev) return;
    futureRef.current.push(snapshotNow());
    applySnapshot(prev);
  }, [items, geofence, setup]);

  const redo = useCallback(() => {
    const next = futureRef.current.pop();
    if (!next) return;
    pastRef.current.push(snapshotNow());
    applySnapshot(next);
  }, [items, geofence, setup]);

  // bump to signal the map to cancel any in-progress geoman draw on Esc
  const [escKey, setEscKey] = useState(0);
  useEffect(() => {
    const onKey = (e) => {
      const el = document.activeElement;
      const typing = el && ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
      const mod = e.ctrlKey || e.metaKey;
      if (typing) return; // let text inputs handle native Ctrl+Z / Esc
      if ((e.key === "z" || e.key === "Z") && mod && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if (((e.key === "Z" || e.key === "y" || e.key === "Y") && mod) || (e.key === "z" && mod && e.shiftKey)) {
        e.preventDefault();
        redo();
        return;
      }
      if (e.key === "Escape") {
        setActiveTool("pointer");
        setEscKey((k) => k + 1);
        setSelectedId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  // whole-plan drag: snapshot geometry at drag start, re-apply + delta per event
  const planSnapshotRef = useRef(null);
  const shift = (v, d) => +(v + d).toFixed(8);
  const handleMovePlanStart = useCallback(() => {
    planSnapshotRef.current = itemsRef.current.map((it) => ({
      id: it.id,
      ...(it.kind === "waypoint"
        ? { latitude: it.latitude, longitude: it.longitude }
        : { polygon: it.polygon, polyline: it.polyline }),
    }));
  }, []);
  const handleTranslatePlanBy = useCallback(({ dLat, dLng }) => {
    const snap = planSnapshotRef.current;
    if (!snap) return;
    setItems((prev) =>
      prev.map((it) => {
        const g = snap.find((s) => s.id === it.id);
        if (!g) return it;
        if (g.latitude != null) {
          return { ...it, latitude: shift(g.latitude, dLat), longitude: shift(g.longitude, dLng) };
        }
        if (g.polygon) {
          return { ...it, polygon: g.polygon.map(([la, ln]) => [shift(la, dLat), shift(ln, dLng)]) };
        }
        if (g.polyline) {
          return { ...it, polyline: g.polyline.map(([la, ln]) => [shift(la, dLat), shift(ln, dLng)]) };
        }
        return it;
      })
    );
  }, []);

  const handleAddWaypoint = useCallback(
    ({ lat, lng }) => {
      if (!setupReady) {
        toast.warn("Complete Launch Setup (home + takeoff elevation) first.");
        return;
      }
      const item = {
        id: uid(),
        kind: "waypoint",
        latitude: +lat.toFixed(8),
        longitude: +lng.toFixed(8),
        altitude: 30,
        hoverDuration: 0,
      };
      commitUndoPoint(() => setItems((prev) => [...prev, item]));
      setSelectedId(item.id);
    },
    [setupReady, commitUndoPoint]
  );

  const handleSetHome = useCallback(
    ({ lat, lng }) => {
      commitUndoPoint(() =>
        setSetup((s) => ({ ...s, homeLocation: { lat: +lat.toFixed(8), lng: +lng.toFixed(8) } }))
      );
      setActiveTool("pointer");
      toast.success("Home location set.");
    },
    [commitUndoPoint]
  );

  const handlePickHome = () => {
    setActiveTool((t) => (t === "home" ? "pointer" : "home"));
  };

  const handleCreateShape = useCallback(
    (shapeKind, coords) => {
      if (!setupReady) {
        toast.warn("Complete Launch Setup (home + takeoff elevation) first.");
        return;
      }
      if (shapeKind === "__fence_in" || shapeKind === "__fence_out") {
        const kind = shapeKind === "__fence_in" ? "fence_inclusion" : "fence_exclusion";
        const item = { id: uid(), kind, polygon: coords, parameters: {} };
        commitUndoPoint(() => {
          setItems((prev) => [...prev, item]);
          setGeofence((g) => ({ ...g, enabled: true }));
        });
        setSelectedId(item.id);
        setActiveTool("pointer");
        toast.success(kind === "fence_inclusion" ? "Inclusion geofence added." : "Exclusion geofence added.");
        return;
      }
      const kindMap = { survey: "survey_grid", corridor: "corridor_scan", structure: "structure_scan" };
      const defaults = {
        survey_grid: { gridAngleDeg: 90, spacing: 10, altitude: 30, turnaroundDistance: 0, crossGrid: false },
        corridor_scan: { width: 20, spacing: 10, altitude: 30 },
        structure_scan: { altitude: 30, offset: 0, levels: 1 },
      };
      const kind = kindMap[shapeKind];
      const item = {
        id: uid(),
        kind,
        ...(kind === "corridor_scan" ? { polyline: coords } : { polygon: coords }),
        parameters: defaults[kind],
      };
      // only ONE mapping pattern per plan — a new drawing replaces the old one
      const hadMapping = itemsRef.current.some((it) => MAPPING_KINDS.includes(it.kind));
      commitUndoPoint(() =>
        setItems((prev) => [...prev.filter((it) => !MAPPING_KINDS.includes(it.kind)), item])
      );
      setSelectedId(item.id);
      setActiveTool("pointer");
      if (hadMapping) {
        toast.info("Previous mapping replaced — only one mapping pattern is allowed.");
      }
    },
    [setupReady, commitUndoPoint]
  );

  const handleUpdateItemGeometry = useCallback((itemId, geometry) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== itemId) return it;
        if (Array.isArray(geometry)) {
          return it.polygon ? { ...it, polygon: geometry } : { ...it, polyline: geometry };
        }
        return {
          ...it,
          latitude: +(geometry.latitude).toFixed(8),
          longitude: +(geometry.longitude).toFixed(8),
        };
      })
    );
  }, []);

  const updateItem = (itemId, patch) =>
    commitUndoPoint(() =>
      setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, ...patch } : it)))
    );

  const commitGeofenceChange = (next) => commitUndoPoint(() => setGeofence(next));
  const commitSetupChange = (next) => commitUndoPoint(() => setSetup(next));

  const deleteItem = (itemId) => {
    commitUndoPoint(() => setItems((prev) => prev.filter((it) => it.id !== itemId)));
    if (selectedId === itemId) setSelectedId(null);
  };

  const selectedIdRef = useRef(null);
  selectedIdRef.current = selectedId;

  // Delete key removes the currently selected item
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Delete") return;
      const el = document.activeElement;
      if (el && ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)) return;
      const id = selectedIdRef.current;
      if (!id) return;
      commitUndoPoint(() => setItems((prev) => prev.filter((it) => it.id !== id)));
      setSelectedId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [commitUndoPoint]);

  /* ── plan persistence ───────────────────────────────────────── */
  const buildPlanDocument = () => ({
    version: 1,
    name: planName,
    items,
    // embed derived polygons so older readers / backend validation keep working
    geofence: { ...geofence, polygons: effectiveGeofence.polygons },
    parameters: {
      takeoffAltitude: setup.takeoffAltitude,
      endAction: setup.rtlAtEnd ? "RTL" : "NONE",
      camera: { interval_s: setup.cameraIntervalS || 0, trigger_type: 0 },
    },
    homeLocation: setup.homeLocation,
  });

  const loadPlanDoc = (doc, planId) => {
    setCurrentPlanId(planId ?? null);
    setPlanName(doc.name || "Untitled Survey");
    const gf = doc.geofence || DEFAULT_GEOFENCE;
    let loaded = doc.items || [];
    const hasFenceItems = loaded.some((it) => FENCE_KINDS.includes(it.kind));
    // migrate legacy plans whose fences lived in geofence.polygons
    if (!hasFenceItems && Array.isArray(gf.polygons)) {
      loaded = [
        ...loaded,
        ...gf.polygons
          .filter((poly) => Array.isArray(poly) && poly.length >= 3)
          .map((poly) => ({
            id: uid(),
            kind: gf.type === "exclusion" ? "fence_exclusion" : "fence_inclusion",
            polygon: poly,
            parameters: {},
          })),
      ];
    }
    setItems(loaded);
    setGeofence(gf);
    const p = doc.parameters || {};
    setSetup({
      homeLocation: doc.homeLocation || null,
      takeoffAltitude: p.takeoffAltitude ?? 20,
      rtlAtEnd: (p.endAction ?? "RTL") === "RTL",
      cameraIntervalS: (p.camera && p.camera.interval_s) || 0,
    });
    setSelectedId(null);
    setFlight(null);
    setFitBoundsKey((k) => k + 1);
    // start a fresh undo history at the newly loaded plan
    pastRef.current = [];
    futureRef.current = [];
  };

  const handleSave = async () => {
    if (!activeProjectId) {
      toast.warn("Select a project before saving a plan.");
      return;
    }
    try {
      const payload = { name: planName, project_id: activeProjectId, plan_document: buildPlanDocument() };
      if (currentPlanId) {
        await updateSurveyPlan(currentPlanId, payload);
        toast.success("Plan updated.");
      } else {
        const created = await createSurveyPlan(payload);
        setCurrentPlanId(created.id);
        toast.success("Plan saved.");
      }
      reloadPlans();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    }
  };

  const handleUpload = async () => {
    if (!activeProjectId) {
      toast.warn("Select a project before creating a plan.");
      return;
    }
    if (!selectedDroneId) {
      toast.warn("Select a drone first.");
      return;
    }
    setUploading(true);
    try {
      let planId = currentPlanId;
      const payload = { name: planName, project_id: activeProjectId, plan_document: buildPlanDocument() };
      if (planId) await updateSurveyPlan(planId, payload);
      else {
        const created = await createSurveyPlan(payload);
        planId = created.id;
        setCurrentPlanId(created.id);
      }
      const result = await uploadSurveyPlan(planId, selectedDroneId);
      setFlight(result.flight || { id: result.flight_id, status: result.status || "staged", message: result.detail });
      const n = result.flight?.total_waypoints;
      toast.success(
        `Mission uploaded${n ? ` — ${n} waypoints` : ""} on drone. Arm and switch to AUTO.`
      );
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleImportKmz = async (file) => {
    if (!activeProjectId) {
      toast.warn("Select a project before importing a plan.");
      return;
    }
    try {
      const created = await importSurveyPlanKmz(file, null, activeProjectId);
      loadPlanDoc(created.plan_document, created.id);
      toast.success(`Imported "${created.name}".`);
      setDrawerOpen(false);
      reloadPlans();
    } catch (e) {
      toast.error(`Import failed: ${e.message}`);
    }
  };

  const handleExportKmz = async (plan) => {
    try {
      const { blob, filename } = await fetchSurveyPlanKmz(plan.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    }
  };

  /* ── render ─────────────────────────────────────────────────── */
  return (
    <div className={`cs-page ${isDarkMode ? "dark" : "light"}`}>
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((p) => !p)}
        isDarkMode={isDarkMode}
        username={username}
        role={role}
        onLogout={handleSignOut}
        hasRole={hasRoleFn}
      />
      <main className="cs-main">
        <Header />
        <div className="cs-toolbar">
          <div className="cs-project-selector" style={{ position: "relative" }}>
            <button
              className="cs-btn"
              style={{ display: "flex", alignItems: "center", gap: "8px", maxWidth: "220px" }}
              onClick={() => setProjectDropdownOpen((p) => !p)}
              title="Select the construction project this plan belongs to"
            >
              <Building2 size={15} color={isDarkMode ? "#F59E0B" : "#0082F8"} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {activeProject?.name || "Select project"}
              </span>
              <ChevronDown size={14} />
            </button>
            {projectDropdownOpen && (
              <div
                className="cs-project-menu"
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  left: 0,
                  minWidth: "240px",
                  maxHeight: "320px",
                  overflowY: "auto",
                  backgroundColor: isDarkMode ? "#161B26" : "#FFFFFF",
                  border: `1px solid ${isDarkMode ? "#252D3D" : "#E5E7EB"}`,
                  borderRadius: "8px",
                  boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
                  zIndex: 200,
                  padding: "6px",
                }}
              >
                <div
                  style={{
                    padding: "6px 8px",
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#9CA3AF",
                    textTransform: "uppercase",
                  }}
                >
                  Select Project
                </div>
                {projects.length === 0 && (
                  <div style={{ padding: "8px 10px", fontSize: "12px", color: "#9CA3AF" }}>
                    No projects available.
                  </div>
                )}
                {projects.map((proj) => (
                  <button
                    key={proj.id}
                    onClick={() => {
                      setCurrentPlanId(null);
                      setPlanName("Untitled Survey");
                      setItems([]);
                      setGeofence(DEFAULT_GEOFENCE);
                      setSelectedId(null);
                      setFlight(null);
                      pastRef.current = [];
                      futureRef.current = [];
                      setActiveProject({
                        id: proj.id,
                        numericId: proj.numericId,
                        name: proj.name,
                        location: proj.location,
                        lastSurvey: proj.lastSurvey || "Latest Survey",
                        imageUrl: proj.imageUrl || null,
                      });
                      setProjectDropdownOpen(false);
                    }}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "8px 10px",
                      borderRadius: "6px",
                      border: "none",
                      backgroundColor:
                        activeProject?.id === proj.id
                          ? isDarkMode
                            ? "#222938"
                            : "#EFF6FF"
                          : "transparent",
                      color: isDarkMode ? "#F3F4F6" : "#111827",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{proj.name}</div>
                    <div style={{ fontSize: "11px", color: "#9CA3AF" }}>{proj.location}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <input
            className="cs-plan-name"
            value={planName}
            onChange={(e) => setPlanName(e.target.value)}
            placeholder="Mission name"
            disabled={noProject}
            title={noProject ? "Select a project first" : "Mission name"}
          />
          {TOOL_BUTTONS.map((b) => {
            const gated = PLANNING_TOOLS.includes(b.key) && !setupReady;
            return (
              <button
                key={b.key}
                className={`cs-btn ${activeTool === b.key ? "active" : ""}`}
                onClick={() => {
                  if (noProject) {
                    toast.warn("Select a project first.");
                    return;
                  }
                  if (gated) {
                    toast.warn("Complete Launch Setup (home + takeoff elevation) first.");
                    return;
                  }
                  setActiveTool(b.key);
                }}
                disabled={noProject || gated}
                title={
                  noProject
                    ? "Select a project first"
                    : gated
                    ? "Complete Launch Setup first"
                    : b.title
                }
              >
                {b.label}
              </button>
            );
          })}
          <button
            className="cs-btn"
            onClick={() => {
              if (noProject) {
                toast.warn("Select a project first.");
                return;
              }
              setFitBoundsKey((k) => k + 1);
            }}
            disabled={noProject}
            title={noProject ? "Select a project first" : "Zoom to plan"}
          >
            ⤢ Fit
          </button>
          <button
            className="cs-btn danger"
            onClick={() => {
              if (noProject) {
                toast.warn("Select a project first.");
                return;
              }
              commitUndoPoint(() => {
                setItems([]);
                setGeofence(DEFAULT_GEOFENCE);
              });
              setCurrentPlanId(null);
              setFlight(null);
              setSelectedId(null);
              setActiveTool("pointer");
            }}
            disabled={noProject}
            title={noProject ? "Select a project first" : "Clear all"}
          >
            ✕ Clear
          </button>
          <div className="cs-spacer" />
          <PlansMenu
            plans={plansList}
            loading={plansLoading}
            disabled={noProject}
            onRefresh={reloadPlans}
            onOpenDrawer={() => {
              setDrawerOpen(true);
              reloadPlans();
            }}
            onImportFile={canWritePlans ? handleImportKmz : undefined}
            onExport={handleExportKmz}
          />
          {canWritePlans && (
            <button
              className="cs-btn"
              onClick={handleSave}
              disabled={noProject}
              title={noProject ? "Select a project first" : "Save plan"}
            >
              💾 Save
            </button>
          )}
          {canWritePlans && canFly && <DroneUploadBar
            drones={drones}
            selectedDroneId={selectedDroneId}
            onDroneChange={setSelectedDroneId}
            onUpload={handleUpload}
            uploading={uploading}
            uploadDisabled={hasBlockingError}
            disabled={noProject}
            flight={flight}
          />}
        </div>

        <div className="cs-body" ref={bodyRef} style={{ position: "relative" }}>
          {noProject && (
            <div
              className="cs-body-veil"
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 100,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: isDarkMode
                  ? "rgba(11,14,20,0.72)"
                  : "rgba(250,250,252,0.78)",
                pointerEvents: "all",
                color: isDarkMode ? "#E5E7EB" : "#374151",
              }}
            >
              <div style={{ textAlign: "center", maxWidth: 320, padding: 16 }}>
                <Building2 size={40} color={isDarkMode ? "#F59E0B" : "#0082F8"} style={{ margin: "0 auto 12px", display: "block" }} />
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
                  Select a project to start planning
                </div>
                <div style={{ fontSize: 12.5, color: isDarkMode ? "#9CA3AF" : "#6B7280" }}>
                  Choose a construction project from the dropdown at the top to load its plans and begin creating a survey.
                </div>
              </div>
            </div>
          )}
          <div className="cs-map-area">
            <SurveyPlanView
              items={items}
              activeTool={activeTool}
              fitBoundsKey={fitBoundsKey}
              setup={setup}
              selectedId={selectedId}
              onSelect={setSelectedId}
              moveWholePlan={activeTool === "move_plan"}
              onAddWaypoint={handleAddWaypoint}
              onCreateShape={handleCreateShape}
              onUpdateItemGeometry={handleUpdateItemGeometry}
              onSetHome={handleSetHome}
              onMovePlanStart={handleMovePlanStart}
              onTranslatePlanBy={handleTranslatePlanBy}
              onGestureStart={beginGesture}
              onGestureEnd={endGesture}
              escKey={escKey}
            />

            {warnings.length > 0 && (
              <div className="cs-warnings">
                {warnings.slice(0, 4).map((w, i) => (
                  <div key={i}>⚠ {w}</div>
                ))}
                {warnings.length > 4 && <div>+{warnings.length - 4} more…</div>}
              </div>
            )}

            <div className="cs-stats">
              <span className="cs-stat-chip">
                Waypoints <b>{stats.waypoint_count}</b>
              </span>
              <span className="cs-stat-chip">
                Distance <b>{stats.total_distance_m} m</b>
              </span>
              <span className="cs-stat-chip">
                Est. time{" "}
                <b>
                  {Math.floor(stats.estimated_time_s / 60)}m{" "}
                  {stats.estimated_time_s % 60}s
                </b>
              </span>
            </div>

            <FlightStatusStrip flight={flight} />
          </div>

          <div
            className="cs-resizer"
            onMouseDown={startResize}
            title="Drag to resize panels"
          />

          <div className="cs-right-panel" style={{ width: editorWidth }}>
            <ItemEditorPanel
              items={items}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onUpdate={updateItem}
              onDelete={deleteItem}
              geofence={geofence}
              onGeofenceChange={commitGeofenceChange}
              setup={setup}
              onSetupChange={commitSetupChange}
              onPickHome={handlePickHome}
              pickingHome={activeTool === "home"}
            />

            <PlansDrawer
              open={drawerOpen}
              plans={plansList}
              loading={plansLoading}
              onClose={() => setDrawerOpen(false)}
              onLoad={(p) => {
                loadPlanDoc(p.plan_document || p, p.id);
                setDrawerOpen(false);
              }}
              onDelete={canWritePlans ? async (id) => {
                try {
                  await deleteSurveyPlan(id);
                  if (id === currentPlanId) setCurrentPlanId(null);
                  reloadPlans();
                } catch (e) {
                  toast.error(`Delete failed: ${e.message}`);
                }
              } : undefined}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

export default ConstructionSurveyPage;
